import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import { Send, Bot, User, RefreshCw, Trash2, ChevronDown, ChevronUp, BookOpen } from 'lucide-react';
import { chatAPI } from '../lib/api';

const ChatTester = ({ agent }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [useRag, setUseRag] = useState(true);  // Changed default to ON so knowledge is used by default
  const scrollRef = useRef(null);

  useEffect(() => {
    // Initialize - no conversation_id yet (will be created on first message)
    setConversationId(null);
    if (agent.widget_settings?.greeting) {
      setMessages([{
        role: 'assistant',
        content: agent.widget_settings.greeting
      }]);
    } else {
      setMessages([]);
    }
  }, [agent]);

  useEffect(() => {
    // Scroll to bottom on new messages
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
    }
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || sending) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setSending(true);

    try {
      const response = await chatAPI.send(
        agent.agent_id, 
        conversationId, 
        userMessage, 
        'dashboard',
        useRag  // Prompt 8: Pass use_rag parameter
      );
      
      // Update conversation_id from response
      if (response.conversation_id && !conversationId) {
        setConversationId(response.conversation_id);
      }
      
      // Add assistant response with sources (Prompt 8)
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: response.answer,
        sources: response.sources || null  // Include sources if available
      }]);
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = error.response?.data?.detail || 
        'Sorry, there was an error processing your message.';
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: errorMessage 
      }]);
    } finally {
      setSending(false);
    }
  };

  const clearChat = () => {
    // Reset conversation - new conversation will be created on next message
    setConversationId(null);
    setMessages(agent.widget_settings?.greeting ? [{
      role: 'assistant',
      content: agent.widget_settings.greeting
    }] : []);
  };

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      {/* Chat Panel */}
      <Card className="bg-slate-900/50 border-slate-800 lg:col-span-2">
        <CardHeader className="border-b border-slate-800">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-white">Test Chat</CardTitle>
              <CardDescription className="text-slate-400">Test your agent's responses</CardDescription>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={clearChat}
                className="border-slate-700 text-slate-300 hover:text-white"
              >
                <RefreshCw className="h-4 w-4 mr-1" />
                New Chat
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {/* Messages */}
          <ScrollArea ref={scrollRef} className="h-[400px] p-4">
            <div className="space-y-4">
              {messages.map((message, index) => (
                <div 
                  key={index} 
                  className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  {message.role === 'assistant' && (
                    <div 
                      className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${agent.widget_settings?.primary_color || '#6366f1'}20` }}
                    >
                      <Bot 
                        className="h-4 w-4" 
                        style={{ color: agent.widget_settings?.primary_color || '#6366f1' }}
                      />
                    </div>
                  )}
                  <div className="max-w-[70%]">
                    <div 
                      className={`rounded-2xl px-4 py-2 ${
                        message.role === 'user' 
                          ? 'bg-indigo-600 text-white' 
                          : 'bg-slate-800 text-slate-200'
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                    </div>
                    {/* Prompt 8: Display sources if available */}
                    {message.role === 'assistant' && message.sources && message.sources.length > 0 && (
                      <Collapsible className="mt-2">
                        <CollapsibleTrigger className="flex items-center gap-2 text-xs text-slate-400 hover:text-slate-300 transition-colors">
                          <BookOpen className="h-3 w-3" />
                          <span>{message.sources.length} source{message.sources.length !== 1 ? 's' : ''}</span>
                          <ChevronDown className="h-3 w-3" />
                        </CollapsibleTrigger>
                        <CollapsibleContent className="mt-2 space-y-2">
                          {message.sources.map((source, idx) => (
                            <div 
                              key={idx} 
                              className="bg-slate-900/50 border border-slate-700 rounded-lg p-3 text-xs"
                            >
                              <div className="flex items-center justify-between mb-1">
                                <Badge variant="outline" className="border-slate-600 text-slate-400 text-xs">
                                  Score: {(source.score * 100).toFixed(1)}%
                                </Badge>
                                <code className="text-xs text-slate-500">
                                  {source.chunk_id?.substring(0, 8)}...
                                </code>
                              </div>
                              <p className="text-slate-300 mt-1 line-clamp-2">
                                {source.snippet}
                              </p>
                              {source.metadata && Object.keys(source.metadata).length > 0 && (
                                <div className="mt-2 pt-2 border-t border-slate-700">
                                  <p className="text-slate-500 text-xs">
                                    {Object.entries(source.metadata).map(([k, v]) => `${k}: ${v}`).join(', ')}
                                  </p>
                                </div>
                              )}
                            </div>
                          ))}
                        </CollapsibleContent>
                      </Collapsible>
                    )}
                  </div>
                  {message.role === 'user' && (
                    <div className="w-8 h-8 rounded-lg bg-slate-700 flex items-center justify-center flex-shrink-0">
                      <User className="h-4 w-4 text-slate-300" />
                    </div>
                  )}
                </div>
              ))}
              {sending && (
                <div className="flex gap-3 justify-start">
                  <div 
                    className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${agent.widget_settings?.primary_color || '#6366f1'}20` }}
                  >
                    <Bot 
                      className="h-4 w-4 animate-pulse" 
                      style={{ color: agent.widget_settings?.primary_color || '#6366f1' }}
                    />
                  </div>
                  <div className="bg-slate-800 rounded-2xl px-4 py-2">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-slate-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                      <span className="w-2 h-2 bg-slate-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                      <span className="w-2 h-2 bg-slate-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>

          {/* Input */}
          <div className="border-t border-slate-800 p-4">
            {/* Prompt 8: RAG Toggle */}
            <div className="flex items-center gap-2 mb-3 pb-3 border-b border-slate-800">
              <Switch
                id="use-rag"
                checked={useRag}
                onCheckedChange={setUseRag}
                className="data-[state=checked]:bg-indigo-600"
              />
              <Label 
                htmlFor="use-rag" 
                className="text-sm text-slate-300 cursor-pointer"
              >
                Use knowledge (RAG)
              </Label>
              {useRag && (
                <Badge variant="outline" className="border-emerald-500/50 text-emerald-400 text-xs ml-auto">
                  RAG Enabled
                </Badge>
              )}
            </div>
            <form 
              onSubmit={(e) => { e.preventDefault(); sendMessage(); }}
              className="flex gap-2"
            >
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type a message..."
                className="bg-slate-800 border-slate-700 text-white"
                disabled={sending}
              />
              <Button 
                type="submit" 
                disabled={!input.trim() || sending}
                className="bg-indigo-600 hover:bg-indigo-500"
              >
                <Send className="h-4 w-4" />
              </Button>
            </form>
          </div>
        </CardContent>
      </Card>

      {/* Info Panel */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader>
          <CardTitle className="text-white text-base">Test Info</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <p className="text-xs text-slate-400 mb-1">Conversation ID</p>
            <code className="text-xs text-slate-300 bg-slate-800 px-2 py-1 rounded block truncate">
              {conversationId || 'New conversation'}
            </code>
          </div>
          
          <div>
            <p className="text-xs text-slate-400 mb-1">Model</p>
            <Badge variant="outline" className="border-slate-700 text-slate-300">
              {agent.model}
            </Badge>
          </div>
          
          <div>
            <p className="text-xs text-slate-400 mb-1">Temperature</p>
            <Badge variant="outline" className="border-slate-700 text-slate-300">
              {agent.temperature}
            </Badge>
          </div>

          <div>
            <p className="text-xs text-slate-400 mb-1">Status</p>
            <Badge 
              variant="outline" 
              className={agent.is_active ? 'border-emerald-500/50 text-emerald-400' : 'border-red-500/50 text-red-400'}
            >
              {agent.is_active ? 'Active' : 'Inactive'}
            </Badge>
          </div>

          <div className="pt-4 border-t border-slate-800">
            <p className="text-xs text-slate-500">
              This is a test environment. Messages are logged but not persisted across sessions.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ChatTester;
